import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { CtaBanner } from "@/components/site/CtaBanner";
import { Reveal } from "@/components/site/Reveal";

import { CheckCircle2, Star, Wrench, Hammer, Droplet, Sparkles, Shield } from "lucide-react";
import metallicImg from "@/assets/service-metallic.jpg";
import flakeImg from "@/assets/service-flake.jpg";
import polishedImg from "@/assets/service-polished.jpg";
import resinousImg from "@/assets/service-resinous.jpg";
import polyaspImg from "@/assets/service-polyaspartic.jpg";
import hardscapeImg from "@/assets/service-hardscape.jpg";
import snowRemovalImg from "@/assets/snow-removal.jpg";
import paversImg from "@/assets/pavers-hardscape.avif.asset.json";
import landscapingImg from "@/assets/commercial-landscaping.avif.asset.json";
import concreteImg from "@/assets/commercial-concrete.avif.asset.json";
import demoImg from "@/assets/service-demo.jpg";
import garageEpoxyImg from "@/assets/garage-epoxy-residential.jpg";
import warehouseImg from "@/assets/service-warehouse.jpg";
import schoolImg from "@/assets/school-epoxy-hallway.jpg";
import restaurantImg from "@/assets/restaurant-epoxy-kitchen.jpg";

export const Route = createFileRoute("/services")({
  head: () => ({
    meta: [
      { title: "Epoxy Flooring Services NJ | Blanco Pro" },
      { name: "description", content: "NJ's top epoxy installers, metallic, flake, polyaspartic & polished concrete. 2-year workmanship warranty. Call 908-895-6133." },

      { property: "og:image", content: "https://blancopros.com/og-image.jpg" },
      { name: "twitter:image", content: "https://blancopros.com/og-image.jpg" },
      { property: "og:title", content: "Epoxy Flooring Services NJ, Blanco Pro Services" },
      { property: "og:description", content: "Metallic, flake, polyaspartic, polished concrete, hardscaping & demolition across NJ." },
      { property: "og:url", content: "https://blancopros.com/services" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Epoxy Flooring Services NJ, Blanco Pro Services" },
      { name: "twitter:description", content: "Metallic, flake, polyaspartic, polished concrete, hardscaping & demolition across NJ." },
    ],
    links: [{ rel: "canonical", href: "https://blancopros.com/services" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Epoxy Flooring Installation",
          provider: { "@id": "https://blancopros.com/#localbusiness" },
          areaServed: { "@type": "State", name: "New Jersey" },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "What's the most popular epoxy finish?", acceptedAnswer: { "@type": "Answer", text: "Decorative flake epoxy is our most popular system, slip-resistant, hides imperfections, and looks great in garages, gyms, and retail." } },
            { "@type": "Question", name: "How long does installation take?", acceptedAnswer: { "@type": "Answer", text: "Most epoxy projects are completed in a 1–3 day window, with the final day reserved for inspection and walk-through." } },
            { "@type": "Question", name: "What's included in the partner network?", acceptedAnswer: { "@type": "Answer", text: "Concrete work, pavers, commercial landscaping, and snow removal are delivered through Blanco Pro's vetted partner network, you get a single point of contact with Blanco managing the project from quote to walk-through." } },
          ],
        }),
      },
    ],
  }),
  component: ServicesPage,
});

type ServiceCategory = "epoxy" | "other";

type Service = {
  title: string;
  img: string;
  tags: string[];
  desc: string;
  partner?: boolean;
  popular?: boolean;
  category: ServiceCategory;
};

const services: Service[] = [
  {
    title: "Flake Epoxy Flooring",
    img: flakeImg,
    tags: ["Residential", "Commercial"],
    desc: "Decorative chip-flake systems that hide imperfections and add texture. Slip-resistant, easy to clean, and built to handle the daily grind of garages, gyms, and retail spaces.",
    category: "epoxy",
    popular: true,
  },
  {
    title: "Metallic / Designer Epoxy",
    img: metallicImg,
    tags: ["Residential", "Commercial"],
    desc: "Mirror-finish floors with custom metallic swirls in silver, pearl, copper, and more. A one-of-a-kind art piece you walk on, perfect for showrooms, barbershops, salons, and luxury garages.",
    category: "epoxy",
  },
  {
    title: "Single Color Epoxy",
    img: polyaspImg,
    tags: ["Residential", "Commercial"],
    desc: "Clean, uniform, high-build epoxy in the color of your choice. The most cost-effective way to get a durable, professional finish on garages, basements, and back-of-house commercial spaces.",
    category: "epoxy",
  },
  {
    title: "Polished Concrete",
    img: polishedImg,
    tags: ["Commercial", "Industrial"],
    desc: "Diamond-ground and densified concrete that reveals a glossy, low-maintenance surface. The gold standard for showrooms, dealerships, and modern retail.",
    category: "epoxy",
  },
  {
    title: "Polyaspartic Coating",
    img: polyaspImg,
    tags: ["Residential", "Commercial"],
    desc: "Cures fast, lasts forever. UV-stable and impact-resistant, back to walking on it within 24 hours. Ideal for garages and any space that can't afford downtime.",
    category: "epoxy",
  },
  {
    title: "Resinous Flooring",
    img: resinousImg,
    tags: ["Commercial", "Industrial"],
    desc: "Seamless, sanitary, chemical-resistant flooring for clinics, kitchens, and labs. Easy to clean and built to meet hygiene standards.",
    category: "epoxy",
  },
  {
    title: "Garage Epoxy Flooring",
    img: garageEpoxyImg,
    tags: ["Residential", "Commercial"],
    desc: "Showroom-grade metallic, flake, and polyaspartic garage floors built for New Jersey homes. Hot-tire resistant, salt-proof, and brighter than any paint kit, most 2-car bays installed in a single day and ready to park on in 24 hours.",
    category: "epoxy",
  },
  {
    title: "Warehouse & Industrial Floor Coating",
    img: warehouseImg,
    tags: ["Commercial", "Industrial"],
    desc: "Heavy-duty 100% solids epoxy and polyaspartic systems engineered for North Jersey distribution centers, logistics hubs, and manufacturing plants. CSP-3 diamond prep, moisture vapor mitigation, chemical-resistant topcoats, and yellow safety-lane striping, built to survive 24/7 forklift traffic.",
    category: "epoxy",
  },
  {
    title: "School Epoxy Flooring",
    img: schoolImg,
    tags: ["Commercial"],
    desc: "Hygienic, slip-resistant, easy-to-clean epoxy systems built for New Jersey schools, classrooms, hallways, cafeterias, locker rooms, libraries, and gymnasiums. Seamless surfaces stand up to heavy student foot traffic, spills, and constant cleaning while staying bright, safe, and on-brand with custom colors.",
    category: "epoxy",
  },
  {
    title: "Restaurant Epoxy Flooring",
    img: restaurantImg,
    tags: ["Commercial"],
    desc: "Seamless, USDA-compliant epoxy and quartz systems engineered for NJ restaurants, kitchens, prep areas, dining rooms, bars, and walk-ins. Slip-resistant, grease- and chemical-resistant, and easy to sanitize. Installed fast to minimize downtime and built to pass every health inspection.",
    category: "epoxy",
  },
  {
    title: "Junk Removal & Demolition",
    img: demoImg,
    tags: ["Residential", "Commercial"],
    desc: "Controlled removal of old concrete, slabs, walkways, and interior structures, plus full residential junk hauling. Backed by two years of hands-on residential demo experience, clean prep work that makes the next phase of your build easier.",
    category: "other",
  },
  {
    title: "Concrete Work",
    img: landscapingImg.url,
    tags: ["Residential", "Commercial"],
    partner: true,
    desc: "Flat slab pours, footings, sidewalks, and driveways delivered through our trusted concrete partner network, decades of field experience, with Blanco managing the relationship from quote to walk-through.",
    category: "other",
  },
  {
    title: "Pavers & Hardscaping",
    img: paversImg.url,
    tags: ["Residential", "Commercial"],
    partner: true,
    desc: "Custom patios, walkways, driveways, and retaining walls in concrete or paver. Installed through our hardscaping partner network so you get specialist craftsmanship with Blanco's single point of contact.",
    category: "other",
  },
  {
    title: "Commercial Landscaping",
    img: concreteImg.url,
    tags: ["Commercial"],
    partner: true,
    desc: "Property maintenance, grounds care, and landscape installation for NJ commercial properties, delivered through our landscaping partner network and bundled with your epoxy or hardscaping project.",
    category: "other",
  },
  {
    title: "Snow Removal",
    img: snowRemovalImg,
    tags: ["Commercial", "Residential"],
    partner: true,
    desc: "Reliable winter snow and ice management for driveways, lots, and walkways. Provided through our seasonal partner network so your property stays safe and accessible all winter long.",
    category: "other",
  },
];





function ServicesPage() {
  const [activeTab, setActiveTab] = useState<ServiceCategory>("epoxy");
  const filtered = services.filter((s) => s.category === activeTab);
  return (
    <>
      {/* HEADER */}
      <section className="pt-40 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-hero relative overflow-hidden">
        <div className="absolute top-20 right-10 w-72 h-72 rounded-full bg-gradient-gold blur-3xl opacity-30 animate-float" />
        <div className="relative max-w-5xl mx-auto text-center animate-fade-up">
          <div className="text-xs uppercase tracking-[0.3em] text-gold mb-4">Our Services</div>
          <h1 className="font-display text-5xl md:text-7xl font-bold leading-[0.95] mb-6">
            Specialized in <em className="not-italic bg-gradient-gold bg-clip-text text-transparent">every finish</em> that elevates your space.
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
            Blanco Pro Services offers expert epoxy flooring, hardscaping, and demolition. Installation, repairs, sealing, and surface preparation for residential and commercial spaces across New Jersey.
          </p>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center mb-12">
            <div className="inline-flex p-1.5 rounded-full bg-secondary border border-border">
              {[
                { id: "epoxy" as const, label: "Epoxy Flooring" },
                { id: "other" as const, label: "Other Services" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${
                    activeTab === tab.id
                      ? "bg-foreground text-background shadow-soft"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {filtered.map((s, i) => (
              <Reveal key={s.title} delay={(i % 2) * 120}>
                <article className="card-tilt rounded-2xl overflow-hidden bg-card border border-border group relative">
                  {s.partner && (
                    <div className="absolute top-4 right-4 z-10 text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full bg-gradient-gold text-gold-foreground shadow-gold font-medium">
                      Partner Network
                    </div>
                  )}
                  {s.popular && (
                    <div className="absolute top-4 right-4 z-10 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full bg-gradient-gold text-gold-foreground shadow-gold font-medium">
                      <Star className="w-3 h-3 fill-current" />
                      Popular
                    </div>
                  )}
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={s.img} alt={s.title} loading="lazy" width={1024} height={768} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-8">
                    <div className="flex gap-2 flex-wrap mb-3">
                      {s.tags.map((t) => (
                        <span key={t} className="text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full bg-secondary text-secondary-foreground">{t}</span>
                      ))}
                    </div>
                    <h2 className="font-display text-2xl md:text-3xl font-bold mb-3">{s.title}</h2>
                    <p className="text-muted-foreground leading-relaxed">{s.desc}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>

          {/* PARTNER NETWORK NOTE */}
          <Reveal>
            <div className="mt-10 rounded-2xl border border-border bg-secondary/50 p-6 text-sm text-muted-foreground leading-relaxed max-w-3xl mx-auto text-center">
              <span className="inline-block text-[10px] uppercase tracking-[0.25em] px-2.5 py-1 rounded-full bg-gradient-gold text-gold-foreground font-medium mr-2 align-middle">Partner Network</span>
              Services marked above are delivered through Blanco Pro Services' trusted partner network. You get the same single point of contact, vetted craftsmanship, and Blanco standard from quote through final walk-through.
            </div>
          </Reveal>
        </div>
      </section>


      {/* PROCESS / TIMELINE */}
      <section className="relative py-28 px-4 sm:px-6 lg:px-8 bg-secondary overflow-hidden">
        {/* decorative gold glow */}
        <div className="pointer-events-none absolute top-1/4 -left-32 w-96 h-96 rounded-full bg-gradient-gold blur-3xl opacity-10" />
        <div className="pointer-events-none absolute bottom-1/4 -right-32 w-96 h-96 rounded-full bg-gradient-gold blur-3xl opacity-10" />

        <div className="relative max-w-6xl mx-auto">
          <Reveal>
            <div className="text-center mb-20 max-w-2xl mx-auto">
              <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">How It Works</div>
              <h2 className="font-display text-4xl md:text-5xl font-bold mb-4">How Epoxy Floor Service Works.</h2>
              <p className="text-muted-foreground text-lg">Five meticulous steps. Concrete to showroom-grade finish.</p>
            </div>
          </Reveal>

          {/* Vertical timeline */}
          <div className="relative">
            {/* center spine */}
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-gold/40 to-transparent -translate-x-1/2" />

            <div className="space-y-16 md:space-y-24">
              {[
                { n: "01", t: "Surface Preparation", icon: Wrench, d: "We comprehensively clean and profile your concrete, crafting the optimal foundation for a bond that lasts for a long time." },
                { n: "02", t: "Crack & Damage Repair", icon: Hammer, d: "Any cracks or spalls are adeptly repaired to guarantee a flawlessly smooth surface." },
                { n: "03", t: "Primer Application", icon: Droplet, d: "A top-quality primer is employed to guarantee the epoxy coating adheres permanently and evenly." },
                { n: "04", t: "Epoxy Coating", icon: Sparkles, d: "We adeptly apply the rich, pigmented epoxy resin, mostly adding decorative flakes for a custom appearance you will love." },
                { n: "05", t: "Protective Topcoat", icon: Shield, d: "A clear, durable topcoat is sealed over everything, offering enhanced resistance to stains and scratches." },
              ].map((p, i) => {
                const isLeft = i % 2 === 0;
                return (
                  <Reveal key={p.n} delay={i * 100}>
                    <div className="relative grid md:grid-cols-2 gap-8 md:gap-16 items-center">
                      {/* timeline node */}
                      <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 z-10 w-14 h-14 rounded-full bg-background border border-gold/40 items-center justify-center shadow-gold">
                        <p.icon className="w-5 h-5 text-gold" />
                      </div>

                      {/* Graphic side */}
                      <div className={`${isLeft ? "md:order-1" : "md:order-2"} group flex items-center justify-center`}>
                        <div className="relative flex items-center justify-center">
                          <div className="absolute w-72 h-72 rounded-full border border-gold/10 group-hover:scale-110 transition-transform duration-700" />
                          <div className="absolute w-56 h-56 rounded-full border border-gold/20 group-hover:scale-110 transition-transform duration-[900ms]" />
                          <div className="absolute w-40 h-40 rounded-full border border-gold/30 group-hover:scale-110 transition-transform duration-[1100ms]" />
                          <div className="absolute w-72 h-72 rounded-full bg-gradient-gold blur-3xl opacity-20 group-hover:opacity-40 transition-opacity duration-700" />
                          <div className="relative w-28 h-28 rounded-full bg-gradient-gold flex items-center justify-center shadow-gold group-hover:scale-110 transition-transform duration-500">
                            <p.icon className="w-12 h-12 text-gold-foreground" strokeWidth={1.5} />
                          </div>
                        </div>
                      </div>

                      {/* Text side */}
                      <div className={`${isLeft ? "md:order-2 md:pl-10" : "md:order-1 md:pr-10 md:text-right"}`}>
                        <div className={`flex items-center gap-3 mb-4 md:hidden`}>
                          <div className="w-11 h-11 rounded-full bg-gradient-gold flex items-center justify-center shadow-gold">
                            <p.icon className="w-5 h-5 text-gold-foreground" />
                          </div>
                          <span className="font-display text-3xl font-bold text-gold">{p.n}</span>
                        </div>
                        <div className={`hidden md:flex items-center gap-2 mb-4 ${isLeft ? "" : "justify-end"}`}>
                          <span className="h-px w-8 bg-gold" />
                          <span className="text-[10px] uppercase tracking-[0.35em] text-gold font-medium">Step {p.n}</span>
                        </div>
                        <h3 className="font-display text-3xl md:text-4xl font-bold mb-4 leading-tight">{p.t}</h3>
                        <p className="text-muted-foreground leading-relaxed text-lg">{p.d}</p>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS - LEAD FORM */}
      <ProcessLeadForm />



      {/* WHY CHOOSE US */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-background">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="text-center mb-14">
              <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Why Choose Blanco Pro</div>
              <h2 className="font-display text-4xl md:text-5xl font-bold">Expertise you can trust.</h2>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { t: "Expertise You Can Trust", d: "Years of experience installing every type of epoxy and hardscape system. Our team delivers precision and excellence on every project." },
              { t: "Premium Materials", d: "We only use top-tier coatings and materials engineered for durability, built to withstand the toughest conditions for years." },
              { t: "Customized Solutions", d: "No two spaces are alike. We tailor every system to your specific needs, style, and functionality requirements." },
              { t: "Customer-Centric", d: "Clear communication, timely updates, and an obsession with quality, from first call to final walk-through." },
            ].map((w, i) => (
              <Reveal key={w.t} delay={(i % 2) * 100}>
                <div className="flex gap-4 p-6 rounded-2xl border border-border bg-card card-tilt">
                  <CheckCircle2 className="w-6 h-6 text-gold shrink-0 mt-1" />
                  <div>
                    <h3 className="font-display text-xl font-bold mb-2">{w.t}</h3>
                    <p className="text-muted-foreground leading-relaxed">{w.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WARRANTY */}
      <section id="warranty" className="scroll-mt-24 py-24 px-4 sm:px-6 lg:px-8 bg-background border-t border-border">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="text-center mb-12">
              <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Warranty</div>
              <h2 className="font-display text-4xl md:text-5xl font-bold mb-4">2-Year Workmanship Warranty</h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                Every install is backed by a straightforward 2-year workmanship warranty. Take good care of the floor and it will last well beyond that.
              </p>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            <Reveal>
              <div className="h-full rounded-2xl border border-border bg-card p-8 shadow-soft">
                <div className="flex items-center gap-3 mb-5">
                  <Shield className="w-6 h-6 text-gold" />
                  <h3 className="font-display text-xl font-bold">What the warranty covers</h3>
                </div>
                <ul className="space-y-3">
                  {[
                    "Peeling or delamination caused by improper installation.",
                    "Failure of the coating due to defects in workmanship.",
                    "Excessive bubbling or lifting resulting from installation issues.",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                      <span className="text-foreground/90 leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={100}>
              <div className="h-full rounded-2xl border border-border bg-card p-8 shadow-soft">
                <div className="flex items-center gap-3 mb-5">
                  <Shield className="w-6 h-6 text-muted-foreground" />
                  <h3 className="font-display text-xl font-bold">What it usually does not cover</h3>
                </div>
                <ul className="space-y-3">
                  {[
                    "Damage from dragging heavy equipment.",
                    "Scratches, tire marks, or normal wear.",
                    "Flooding, moisture coming up through the concrete, or structural movement.",
                    "Chemical abuse or improper cleaning.",
                    "Damage caused by other contractors.",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className="w-5 h-5 rounded-full border border-border flex items-center justify-center shrink-0 mt-0.5 text-muted-foreground text-xs">×</span>
                      <span className="text-foreground/90 leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-secondary border-t border-border">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <div className="text-center mb-12">
              <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">FAQ</div>
              <h2 className="font-display text-4xl md:text-5xl font-bold">Quick answers, no fluff.</h2>
            </div>
          </Reveal>
          <div className="space-y-4">
            {[
              { q: "How long does an epoxy floor installation take?", a: "Most projects wrap in a 1–3 day window, with the final day reserved for inspection and walk-through. Polyaspartic systems can return to service in 24 hours." },
              { q: "What's a 'Partner Network' service?", a: "Concrete work, pavers, commercial landscaping, and snow removal are delivered through our vetted partner network. You get a single point of contact, with Blanco managing the relationship from quote to walk-through." },
              { q: "Where do you source your epoxy materials?", a: "We source from established New Jersey resin manufacturers like Master Bond (Hackensack), A & C Catalysts (Linden), and Cardolite (Monmouth Junction), keeping lead times short and chemistry fresh." },
              { q: "What areas do you serve?", a: "Every county in New Jersey, plus eastern Pennsylvania and parts of New York." },
            ].map((f) => (
              <details key={f.q} className="group rounded-2xl border border-border bg-card p-6 open:shadow-soft transition-all">
                <summary className="cursor-pointer font-display text-lg font-semibold list-none flex items-center justify-between gap-4">
                  {f.q}
                  <span className="text-gold text-xl transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-3 text-muted-foreground leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner />
    </>
  );
}

const ELROI_FORM_SCRIPT = "https://links.elroi.marketing/js/form_embed.js";

function ProcessLeadForm() {
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (document.querySelector(`script[src="${ELROI_FORM_SCRIPT}"]`)) return;
    const s = document.createElement("script");
    s.src = ELROI_FORM_SCRIPT;
    s.async = true;
    document.body.appendChild(s);
  }, []);

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-background">
      <div className="max-w-3xl mx-auto">
        <Reveal>
          <div className="text-center mb-10">
            <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Get Started</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold mb-4">Request Your Free Estimate</h2>
            <p className="text-muted-foreground text-lg">Tell us about your project, we'll be in touch fast.</p>
          </div>
        </Reveal>
        <Reveal delay={100}>
          <div className="bg-card border border-border rounded-3xl p-4 sm:p-6 shadow-soft">
            <iframe
              src="https://links.elroi.marketing/widget/form/VYJE0VeyZbNm6Fjb6wqn"
              style={{ width: "100%", minHeight: "600px", border: "none", borderRadius: "3px" }}
              id="inline-VYJE0VeyZbNm6Fjb6wqn"
              data-layout="{'id':'INLINE'}"
              data-trigger-type="alwaysShow"
              data-trigger-value=""
              data-activation-type="alwaysActivated"
              data-activation-value=""
              data-deactivation-type="neverDeactivate"
              data-deactivation-value=""
              data-form-name="How Epoxy Floor Service Works - Contact Form"
              data-height="undefined"
              data-layout-iframe-id="inline-VYJE0VeyZbNm6Fjb6wqn"
              data-form-id="VYJE0VeyZbNm6Fjb6wqn"
              title="How Epoxy Floor Service Works - Contact Form"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

